import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Edit2 } from 'lucide-react';
import { useAuth } from '../App';

const WEEKDAYS  = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
const MONTHS_DE = ['Januar','Februar','März','April','Mai','Juni','Juli','August','September','Oktober','November','Dezember'];

function dateKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}
function isSameDay(a, b) {
  return a.getFullYear()===b.getFullYear() && a.getMonth()===b.getMonth() && a.getDate()===b.getDate();
}
function getWeekNumber(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  const y = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - y) / 86400000) + 1) / 7);
}

function buildDefaultNotes() {
  const today = new Date();
  const notes = {};
  const add = (offset, text) => {
    const d = new Date(today);
    d.setDate(today.getDate() + offset);
    notes[dateKey(d)] = text;
  };
  add(0,  '📌 Voice Profile aktualisieren');
  add(2,  '📝 Post über Leistungen generieren');
  add(7,  '💡 Wochenplanung: 3 Posts für nächste Woche');
  return notes;
}

const STORED_NOTES_KEY = 'ct_cal_notes';

export function GlassCalendar({ posts = [], scheduledPosts = [] }) {
  const navigate   = useNavigate();
  const { token }  = useAuth();
  const today      = useMemo(() => new Date(), []);

  const [view,         setView]         = useState('weekly');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [editingNote,  setEditingNote]  = useState(false);
  const [noteInput,    setNoteInput]    = useState('');
  const [notes,        setNotes]        = useState({});
  const [notesSynced,  setNotesSynced]  = useState(false);

  // Notizen beim Start vom Backend laden, localStorage als Fallback
  useEffect(() => {
    if (!token) return;
    fetch('/api/calendar/notes', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(serverNotes => {
        if (Object.keys(serverNotes).length > 0) {
          // Server hat Daten — verwenden und localStorage aktualisieren
          setNotes(serverNotes);
          localStorage.setItem(STORED_NOTES_KEY, JSON.stringify(serverNotes));
        } else {
          // Server leer — localStorage-Daten migrieren falls vorhanden
          const local = localStorage.getItem(STORED_NOTES_KEY);
          if (local) {
            const parsed = JSON.parse(local);
            setNotes(parsed);
            // Lokale Notizen auf Server hochladen
            Object.entries(parsed).forEach(([date, text]) => {
              if (text) {
                fetch('/api/calendar/notes', { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ date, text }) }).catch(() => {});
              }
            });
          } else {
            // Frisch — Beispiel-Notizen setzen (nur lokal, nicht speichern)
            setNotes(buildDefaultNotes());
          }
        }
        setNotesSynced(true);
      })
      .catch(() => {
        // Offline-Fallback: localStorage
        try {
          const stored = localStorage.getItem(STORED_NOTES_KEY);
          if (stored) setNotes(JSON.parse(stored));
          else setNotes(buildDefaultNotes());
        } catch { setNotes(buildDefaultNotes()); }
        setNotesSynced(true);
      });
  }, [token]);

  const saveNotes = useCallback((updated, dateToSave, textToSave) => {
    const clean = Object.fromEntries(Object.entries(updated).filter(([,v]) => v != null));
    setNotes(clean);
    localStorage.setItem(STORED_NOTES_KEY, JSON.stringify(clean));
    // Auf Server speichern
    if (token && dateToSave !== undefined) {
      fetch('/api/calendar/notes', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ date: dateToSave, text: textToSave || '' })
      }).catch(() => {}); // Fehler still — localStorage ist Fallback
    }
  }, [token]);

  const postsByDate = useMemo(() => {
    const map = {};
    posts.forEach(post => {
      if (!post.created_at) return;
      const k = dateKey(new Date(post.created_at));
      if (!map[k]) map[k] = [];
      map[k].push(post);
    });
    return map;
  }, [posts]);

  const scheduledByDate = useMemo(() => {
    const map = {};
    scheduledPosts.forEach(sp => {
      if (!sp.scheduled_at) return;
      const k = dateKey(new Date(sp.scheduled_at));
      if (!map[k]) map[k] = [];
      map[k].push(sp);
    });
    return map;
  }, [scheduledPosts]);

  function makeDay(date) {
    const k = dateKey(date);
    return {
      date, key: k,
      isToday:    isSameDay(date, today),
      isSelected: isSameDay(date, selectedDate),
      weekday:    (date.getDay()+6)%7,
      posts:      postsByDate[k] || [],
      scheduled:  scheduledByDate[k] || [],
      hasNote:    !!notes[k],
    };
  }

  const weekDays = useMemo(() => {
    const dow    = (selectedDate.getDay()+6)%7;
    const monday = new Date(selectedDate);
    monday.setDate(selectedDate.getDate() - dow);
    return Array.from({length:7}, (_,i) => {
      const d = new Date(monday); d.setDate(monday.getDate()+i); return makeDay(d);
    });
  }, [selectedDate, postsByDate, scheduledByDate, notes, today]);

  const monthGrid = useMemo(() => {
    const y = selectedDate.getFullYear();
    const m = selectedDate.getMonth();
    const firstWd = (new Date(y, m, 1).getDay()+6)%7;
    const total   = new Date(y, m+1, 0).getDate();
    const cells   = [];
    for (let i = 0; i < firstWd; i++) cells.push(null);
    for (let d = 1; d <= total; d++) cells.push(makeDay(new Date(y, m, d)));
    return cells;
  }, [selectedDate, postsByDate, scheduledByDate, notes, today]);

  function goBack() {
    const d = new Date(selectedDate);
    if (view==='weekly') d.setDate(d.getDate()-7);
    else { d.setDate(1); d.setMonth(d.getMonth()-1); }
    setSelectedDate(d);
  }
  function goForward() {
    const d = new Date(selectedDate);
    if (view==='weekly') d.setDate(d.getDate()+7);
    else { d.setDate(1); d.setMonth(d.getMonth()+1); }
    setSelectedDate(d);
  }

  const selectedKey       = dateKey(selectedDate);
  const selectedPosts     = postsByDate[selectedKey] || [];
  const selectedScheduled = scheduledByDate[selectedKey] || [];
  const selectedNote      = notes[selectedKey] || '';

  function handleDateClick(day) {
    setSelectedDate(day.date);
    setNoteInput(notes[day.key] || '');
    setEditingNote(false);
  }
  function saveNote() {
    const newText = noteInput.trim() || undefined;
    saveNotes({ ...notes, [selectedKey]: newText }, selectedKey, noteInput.trim());
    setEditingNote(false);
  }

  const headerTitle = view === 'weekly'
    ? (() => {
        const f = weekDays[0]?.date, l = weekDays[6]?.date;
        if (!f||!l) return '';
        return f.getMonth()===l.getMonth()
          ? `${f.getDate()}. – ${l.getDate()}. ${MONTHS_DE[l.getMonth()].slice(0,3)}`
          : `${f.getDate()}. ${MONTHS_DE[f.getMonth()].slice(0,3)} – ${l.getDate()}. ${MONTHS_DE[l.getMonth()].slice(0,3)}`;
      })()
    : MONTHS_DE[selectedDate.getMonth()];

  function DayDots({ day }) {
    const hasPost  = day.posts.length > 0;
    const hasSched = day.scheduled.length > 0;
    if (!hasPost && !hasSched) return null;
    return (
      <div style={{ position:'absolute', bottom:2, left:'50%', transform:'translateX(-50%)', display:'flex', gap:2 }}>
        {hasPost  && <div style={{ width:4, height:4, borderRadius:'50%', background:'var(--cal-cyan)', boxShadow:'0 0 4px var(--cal-cyan)' }} />}
        {hasSched && <div style={{ width:4, height:4, borderRadius:'50%', background:'var(--cal-sched)', boxShadow:'0 0 4px var(--cal-sched)' }} />}
      </div>
    );
  }

  return (
    <div style={{ background:'var(--cal-bg)', backdropFilter:'blur(24px)', border:'1px solid var(--cal-border)', borderRadius:20, padding:'20px 22px', width:'100%', minWidth:0 }}>
      {/* Tabs + Navigation */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
        <div style={{ display:'flex', gap:3, background:'var(--cal-tab-bg)', borderRadius:9, padding:3 }}>
          {[['weekly','Woche'],['monthly','Monat']].map(([v,label]) => (
            <button key={v} onClick={() => setView(v)} style={{ padding:'6px 16px', borderRadius:7, fontSize:13, fontWeight:700, fontFamily:'Outfit', cursor:'pointer', transition:'all 0.15s', background:view===v ? 'var(--cal-tab-active-bg)' : 'transparent', color:view===v ? 'var(--cal-tab-active-text)' : 'var(--cal-muted)', border:'none' }}>{label}</button>
          ))}
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
          <span style={{ fontSize:13, color:'var(--cal-text2)', fontFamily:'Outfit', marginRight:4 }}>
            {view==='monthly' ? selectedDate.getFullYear() : `KW ${getWeekNumber(selectedDate)}`}
          </span>
          {[goBack, goForward].map((fn,i) => (
            <button key={i} onClick={fn} style={{ width:30, height:30, borderRadius:'50%', background:'var(--cal-btn-bg)', border:'1px solid var(--cal-border)', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', color:'var(--cal-text2)' }}>
              {i===0 ? <ChevronLeft size={15}/> : <ChevronRight size={15}/>}
            </button>
          ))}
        </div>
      </div>

      <div style={{ marginBottom:16 }}>
        <div style={{ fontSize:view==='weekly' ? 22 : 30, fontFamily:'Orbitron', fontWeight:800, color:'var(--cal-title)', lineHeight:1 }}>
          {headerTitle}
        </div>
      </div>

      {/* ── WOCHE ── */}
      {view==='weekly' && (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:6, marginBottom:4 }}>
          {weekDays.map(day => (
            <div key={day.key} onClick={() => handleDateClick(day)} style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:5, cursor:'pointer', padding:'4px 2px' }}>
              <span style={{ fontSize:11, fontWeight:700, color:day.isSelected ? 'var(--cal-cyan)' : 'var(--cal-muted)' }}>{WEEKDAYS[day.weekday]}</span>
              <div style={{ width:44, height:44, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:15, fontWeight:day.isSelected||day.isToday ? 700 : 500, position:'relative', transition:'all 0.15s',
                background: day.isSelected ? 'linear-gradient(135deg,#ff6b6b,#ff9500)' : day.isToday ? 'var(--cal-today-bg)' : day.posts.length>0 ? 'var(--cal-post-bg)' : 'transparent',
                border: day.isSelected ? 'none' : day.isToday ? '2px solid var(--cal-cyan)' : day.posts.length>0 ? '1px solid var(--cal-post-border)' : '1px solid transparent',
                color: day.isSelected ? '#fff' : day.isToday ? 'var(--cal-cyan)' : 'var(--cal-text)',
                boxShadow: day.isSelected ? '0 4px 14px rgba(255,100,50,0.45)' : 'none'
              }}>
                {day.date.getDate()}
                {!day.isSelected && <DayDots day={day} />}
                {day.hasNote && !day.isSelected && <div style={{ position:'absolute', top:1, right:1, width:7, height:7, borderRadius:'50%', background:'#ff9500' }} />}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── MONAT ── */}
      {view==='monthly' && (
        <div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:3, marginBottom:6 }}>
            {WEEKDAYS.map(d => <div key={d} style={{ fontSize:11, fontWeight:700, color:'var(--cal-muted)', textAlign:'center', padding:'3px 0' }}>{d}</div>)}
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:4 }}>
            {monthGrid.map((day, i) => !day ? <div key={`e-${i}`} /> : (
              <div key={day.key} onClick={() => handleDateClick(day)} style={{ display:'flex', justifyContent:'center', cursor:'pointer' }}>
                <div style={{ width:'100%', aspectRatio:'1', maxWidth:44, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, fontWeight:day.isSelected||day.isToday ? 700 : 400, position:'relative', margin:'0 auto',
                  background: day.isSelected ? 'linear-gradient(135deg,#ff6b6b,#ff9500)' : day.isToday ? 'var(--cal-today-bg)' : day.posts.length>0 ? 'var(--cal-post-bg)' : 'transparent',
                  border: day.isSelected ? 'none' : day.isToday ? '2px solid var(--cal-cyan)' : day.posts.length>0 ? '1px solid var(--cal-post-border)' : '1px solid transparent',
                  color: day.isSelected ? '#fff' : day.isToday ? 'var(--cal-cyan)' : 'var(--cal-text)',
                  boxShadow: day.isSelected ? '0 2px 8px rgba(255,100,50,0.4)' : 'none'
                }}>
                  {day.date.getDate()}
                  {!day.isSelected && <DayDots day={day} />}
                  {day.hasNote && !day.isSelected && <div style={{ position:'absolute', top:0, right:0, width:5, height:5, borderRadius:'50%', background:'#ff9500' }} />}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ height:1, background:'var(--cal-divider)', margin:'16px 0 14px' }} />

      {/* ── Tag-Details ── */}
      <div>
        <div style={{ fontSize:13, fontWeight:600, color:'var(--cal-text2)', marginBottom:10 }}>
          {selectedDate.toLocaleDateString('de-DE', { weekday:'long', day:'numeric', month:'long' })}
        </div>

        {/* Geplante Posts */}
        {selectedScheduled.length > 0 && (
          <div style={{ marginBottom: selectedPosts.length > 0 ? 8 : 10 }}>
            <div style={{ fontSize:10, color:'var(--cal-sched)', letterSpacing:'0.08em', fontWeight:600, marginBottom:6 }}>GEPLANT</div>
            <div style={{ display:'flex', flexDirection:'column', gap:5 }}>
              {selectedScheduled.map(sp => (
                <div key={sp.variant_id} onClick={() => navigate('/history', { state: { expandPostId: sp.post_id } })}
                  style={{ display:'flex', alignItems:'center', gap:8, padding:'7px 12px', background:'var(--cal-sched-bg)', border:'1px solid var(--cal-sched-border)', borderRadius:9, cursor:'pointer' }}>
                  <span style={{ fontSize:10 }}>📅</span>
                  <span style={{ fontSize:12, color:'var(--cal-text)', flex:1 }} className="truncate">{sp.topic}</span>
                  <span style={{ fontSize:10, color:'var(--cal-sched)', flexShrink:0 }}>
                    {new Date(sp.scheduled_at).toLocaleTimeString('de-DE', { hour:'2-digit', minute:'2-digit' })} Uhr
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {selectedPosts.length > 0 ? (
          <div style={{ display:'flex', flexDirection:'column', gap:6, marginBottom:10 }}>
            {selectedScheduled.length > 0 && <div style={{ fontSize:10, color:'var(--cal-cyan)', letterSpacing:'0.08em', fontWeight:600, marginBottom:2 }}>ERSTELLT</div>}
            {selectedPosts.map(post => (
              <div key={post.id} onClick={() => navigate('/history', { state: { expandPostId: post.id } })}
                style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 12px', background:'var(--cal-post-item-bg)', border:'1px solid var(--cal-post-item-border)', borderRadius:9, cursor:'pointer' }}>
                <span className="dot-live" style={{ width:6, height:6, flexShrink:0 }} />
                <span style={{ fontSize:13, color:'var(--cal-text)', flex:1 }} className="truncate">{post.topic}</span>
                <span style={{ fontSize:11, color:'var(--cal-cyan)', flexShrink:0 }}>Öffnen →</span>
              </div>
            ))}
          </div>
        ) : selectedScheduled.length === 0 ? (
          <p style={{ fontSize:13, color:'var(--cal-muted)', fontStyle:'italic', marginBottom:10 }}>Kein Post an diesem Tag.</p>
        ) : null}

        {/* Notiz */}
        {editingNote ? (
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            <textarea value={noteInput} onChange={e => setNoteInput(e.target.value)} placeholder="Notiz hinzufügen..." rows={3} autoFocus
              onKeyDown={e => { if(e.key==='Enter'&&e.ctrlKey) saveNote(); if(e.key==='Escape') setEditingNote(false); }}
              style={{ width:'100%', background:'var(--cal-input-bg)', border:'1px solid var(--cal-input-border)', borderRadius:9, padding:'9px 12px', color:'var(--cal-text)', fontSize:13, fontFamily:'Outfit', resize:'none', outline:'none', lineHeight:1.6 }} />
            <div style={{ display:'flex', gap:8 }}>
              <button onClick={saveNote} className="btn-primary" style={{ fontSize:12, padding:'6px 18px' }}>Speichern</button>
              <button onClick={() => setEditingNote(false)} className="btn-ghost" style={{ fontSize:12 }}>Abbrechen</button>
            </div>
          </div>
        ) : (
          <div style={{ display:'flex', alignItems:'flex-start', gap:8, padding:'8px 12px', background:'var(--cal-input-bg)', borderRadius:9, border:'1px solid var(--cal-border)', cursor:'pointer', minHeight:40 }}
            onClick={() => { setNoteInput(selectedNote); setEditingNote(true); }}>
            <Edit2 size={14} style={{ color:'var(--cal-muted)', flexShrink:0, marginTop:1 }} />
            <div style={{ flex:1, minWidth:0 }}>
              {selectedNote
                ? <p style={{ fontSize:13, color:'var(--cal-text2)', lineHeight:1.5, whiteSpace:'pre-wrap' }}>{selectedNote}</p>
                : <p style={{ fontSize:13, color:'var(--cal-muted)', fontStyle:'italic' }}>Notiz hinzufügen...</p>}
            </div>
            {selectedNote && (
              <button onClick={e => { e.stopPropagation(); saveNotes({ ...notes, [selectedKey]: undefined }, selectedKey, ''); }}
                style={{ fontSize:12, color:'var(--red)', background:'none', border:'none', cursor:'pointer', flexShrink:0 }}>✕</button>
            )}
          </div>
        )}
      </div>

      {/* Legende */}
      <div style={{ display:'flex', gap:14, marginTop:12, flexWrap:'wrap' }}>
        {[
          ['var(--cal-cyan)', 'Post erstellt'],
          ['var(--cal-sched)', 'Geplant'],
          ['#ff9500', 'Notiz'],
          ['linear-gradient(135deg,#ff6b6b,#ff9500)', 'Ausgewählt'],
        ].map(([bg, label]) => (
          <div key={label} style={{ display:'flex', alignItems:'center', gap:6 }}>
            <div style={{ width:8, height:8, borderRadius:'50%', background:bg, flexShrink:0 }} />
            <span style={{ fontSize:10, color:'var(--cal-muted)', fontFamily:'Outfit' }}>{label}</span>
          </div>
        ))}
      </div>

      {/* ──────────────────────────────────────────────────────────────
          CSS: :root ist html-Element (wo data-theme gesetzt wird)
          NICHT body — weil App.js data-theme auf documentElement setzt
      ────────────────────────────────────────────────────────────── */}
      <style>{`
        :root:not([data-theme="light"]) {
          --cal-bg: rgba(5,8,20,0.75);
          --cal-border: rgba(0,200,240,0.18);
          --cal-tab-bg: rgba(0,0,0,0.25);
          --cal-tab-active-bg: #ffffff;
          --cal-tab-active-text: #000000;
          --cal-btn-bg: rgba(255,255,255,0.05);
          --cal-title: #ffffff;
          --cal-text: rgba(255,255,255,0.9);
          --cal-text2: rgba(255,255,255,0.55);
          --cal-muted: rgba(255,255,255,0.3);
          --cal-cyan: #00ffff;
          --cal-today-bg: rgba(0,255,255,0.12);
          --cal-post-bg: rgba(0,255,255,0.07);
          --cal-post-border: rgba(0,255,255,0.22);
          --cal-divider: rgba(255,255,255,0.08);
          --cal-post-item-bg: rgba(0,255,255,0.07);
          --cal-post-item-border: rgba(0,255,255,0.2);
          --cal-input-bg: rgba(255,255,255,0.04);
          --cal-input-border: rgba(0,255,255,0.15);
          --cal-sched: #a78bfa;
          --cal-sched-bg: rgba(167,139,250,0.08);
          --cal-sched-border: rgba(167,139,250,0.25);
        }
        :root[data-theme="light"] {
          --cal-bg: rgba(255,255,255,0.9);
          --cal-border: rgba(0,100,180,0.15);
          --cal-tab-bg: rgba(0,0,0,0.06);
          --cal-tab-active-bg: #0066cc;
          --cal-tab-active-text: #ffffff;
          --cal-btn-bg: rgba(0,0,0,0.04);
          --cal-title: #0a0a1a;
          --cal-text: #0a0a1a;
          --cal-text2: rgba(10,10,26,0.65);
          --cal-muted: rgba(10,10,26,0.4);
          --cal-cyan: #0055bb;
          --cal-today-bg: rgba(0,85,187,0.1);
          --cal-post-bg: rgba(0,85,187,0.07);
          --cal-post-border: rgba(0,85,187,0.25);
          --cal-divider: rgba(0,0,0,0.08);
          --cal-post-item-bg: rgba(0,85,187,0.06);
          --cal-post-item-border: rgba(0,85,187,0.18);
          --cal-input-bg: rgba(0,0,0,0.03);
          --cal-input-border: rgba(0,85,187,0.2);
          --cal-sched: #6d28d9;
          --cal-sched-bg: rgba(109,40,217,0.08);
          --cal-sched-border: rgba(109,40,217,0.25);
        }
      `}</style>
    </div>
  );
}
